import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { PageTitle } from '../../../../../_metronic/layout/core'
import Swal from 'sweetalert2'
import { ReportShell } from '../components/ReportShell'
import { SellerIdentity } from '../components/SellerIdentity'
import { loadSellerTicketAverageReport } from '../services'
import {
  SellerReportSourceFilter,
  SellerTicketAverageFilters,
  SellerTicketAverageReport,
} from '../types'
import * as XLSX from 'xlsx'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'

interface SellerConcentrationRow {
  sellerKey: string
  sellerLabel: string
  orderCount: number
  totalAmountCents: number
  averageTicketCents: number
  revenueSharePercent: number
  cumulativeRevenueSharePercent: number
  curveClass: 'A' | 'B' | 'C'
}

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
})

const percentFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
})

const today = new Date()
const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1)

const toDateInputValue = (date: Date): string => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const formatCurrency = (amountCents: number): string => currencyFormatter.format(amountCents / 100)

const buildConcentrationRows = (report: SellerTicketAverageReport | null): SellerConcentrationRow[] => {
  if (!report?.rows.length) {
    return []
  }

  const totalAmountCents = report.summary.totalAmountCents || 0
  let cumulativeRevenueSharePercent = 0

  return [...report.rows]
    .sort((left, right) => right.totalAmountCents - left.totalAmountCents)
    .map((row) => {
      const revenueSharePercent =
        totalAmountCents > 0 ? (row.totalAmountCents / totalAmountCents) * 100 : 0

      cumulativeRevenueSharePercent += revenueSharePercent

      let curveClass: 'A' | 'B' | 'C' = 'C'
      if (cumulativeRevenueSharePercent <= 80) {
        curveClass = 'A'
      } else if (cumulativeRevenueSharePercent <= 95) {
        curveClass = 'B'
      }

      return {
        sellerKey: row.sellerKey,
        sellerLabel: row.sellerLabel,
        orderCount: row.orderCount,
        totalAmountCents: row.totalAmountCents,
        averageTicketCents: row.averageTicketCents,
        revenueSharePercent,
        cumulativeRevenueSharePercent,
        curveClass,
      }
    })
}

const curveBadgeClass = (curveClass: 'A' | 'B' | 'C'): string => {
  if (curveClass === 'A') {
    return 'badge badge-light-success'
  }

  if (curveClass === 'B') {
    return 'badge badge-light-warning'
  }

  return 'badge badge-light-danger'
}

const exportToCSV = (filename: string, rows: SellerConcentrationRow[]): void => {
  let csv = 'sep=;\n'
  csv += 'Posição;Vendedor;Pedidos;Faturamento;Ticket médio;Participação;Acumulado;Curva\n'

  rows.forEach((row, index) => {
    csv += `${index + 1};"${row.sellerLabel}";${row.orderCount};${row.totalAmountCents / 100};${row.averageTicketCents / 100};${row.revenueSharePercent.toFixed(1)};${row.cumulativeRevenueSharePercent.toFixed(1)};${row.curveClass}\n`
  })

  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)
  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const exportToXLSX = (filename: string, rows: SellerConcentrationRow[]): void => {
  const data = rows.map((row, index) => ({
    Posição: index + 1,
    Vendedor: row.sellerLabel,
    Pedidos: row.orderCount,
    Faturamento: row.totalAmountCents / 100,
    'Ticket médio': row.averageTicketCents / 100,
    Participação: row.revenueSharePercent.toFixed(1),
    Acumulado: row.cumulativeRevenueSharePercent.toFixed(1),
    Curva: row.curveClass,
  }))

  const worksheet = XLSX.utils.json_to_sheet(data)
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Curva ABC')
  XLSX.writeFile(workbook, filename)
}

const exportToPDF = (
  filename: string,
  rows: SellerConcentrationRow[],
  report: SellerTicketAverageReport | null,
  curveAShare: number
): void => {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  const margin = 15
  let yPosition = margin

  pdf.setFontSize(16)
  pdf.text('Relatório de Concentração por Vendedor', margin, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setTextColor(100, 100, 100)
  pdf.text(`Relatório gerado em: ${new Date().toLocaleString('pt-BR')}`, margin, yPosition)
  yPosition += 12

  pdf.setFontSize(11)
  pdf.setTextColor(0, 0, 0)
  pdf.text('Indicadores Consolidados', margin, yPosition)
  yPosition += 6

  pdf.setFontSize(9)
  pdf.text(`Receita total: ${formatCurrency(report?.summary.totalAmountCents || 0)} | Curva A: ${percentFormatter.format(curveAShare)}%`, margin, yPosition)
  yPosition += 10

  autoTable(pdf, {
    startY: yPosition,
    head: [['Pos', 'Vendedor', 'Pedidos', 'Faturamento', 'Ticket médio', 'Participação', 'Acumulado', 'Curva']],
    body: rows.map((row, index) => [
      index + 1,
      row.sellerLabel,
      row.orderCount,
      formatCurrency(row.totalAmountCents),
      formatCurrency(row.averageTicketCents),
      row.revenueSharePercent.toFixed(1),
      row.cumulativeRevenueSharePercent.toFixed(1),
      row.curveClass,
    ]),
    margin: { left: margin, right: margin },
    headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, fontStyle: 'bold' },
    bodyStyles: { fontSize: 8, textColor: 50 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
  })

  const pageCount = (pdf as any).internal.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Página ${i} de ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' })
  }

  pdf.save(filename)
}

const SellerConcentrationReportPage: FC = () => {
  const [filters, setFilters] = useState<SellerTicketAverageFilters>({
    startDate: toDateInputValue(firstDayOfMonth),
    finalDate: toDateInputValue(today),
    source: 'all',
  })
  const [loading, setLoading] = useState(false)
  const [report, setReport] = useState<SellerTicketAverageReport | null>(null)

  const loadReport = useCallback(async () => {
    try {
      setLoading(true)
      const data = await loadSellerTicketAverageReport(filters)
      setReport(data)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar relatório',
        text: error instanceof Error ? error.message : 'Erro desconhecido',
        confirmButtonText: 'OK',
      })
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    void loadReport()
  }, [loadReport])

  const sourceOptions = useMemo(
    () => [
      { value: 'all' as const, label: 'Todas as fontes' },
      { value: 'ninegrid' as const, label: 'Ninegrid' },
      { value: 'totvs' as const, label: 'TOTVS' },
    ],
    []
  )

  const rows = useMemo(() => buildConcentrationRows(report), [report])

  const topSeller = rows[0] || null
  const curveARevenue = rows
    .filter((row) => row.curveClass === 'A')
    .reduce((accumulator, row) => accumulator + row.totalAmountCents, 0)
  const curveAShare =
    (report?.summary.totalAmountCents || 0) > 0
      ? (curveARevenue / (report?.summary.totalAmountCents || 1)) * 100
      : 0

  const onSourceChange = (source: SellerReportSourceFilter) => {
    setFilters((previous) => ({
      ...previous,
      source,
    }))
  }

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Concentração por Vendedor',
            path: '/seller/reports/seller-concentration',
            isActive: true,
          },
        ]}
      >
        Relatório de Concentração por Vendedor
      </PageTitle>

      <ReportShell
        title='Concentração de Receita por Vendedor'
        description='Participação individual, participação acumulada e classificação ABC com base no faturamento do período.'
        actions={
          <div className='d-flex gap-2'>
            <button className='btn btn-primary' onClick={loadReport} disabled={loading}>
              <i className='fas fa-sync-alt me-2'></i>
              {loading ? 'Carregando...' : 'Atualizar'}
            </button>
            <button className='btn btn-light-success' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToCSV(`Concentracao-Vendedor-${timestamp}.csv`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em CSV'>
              <i className='fas fa-file-csv me-2'></i> CSV
            </button>
            <button className='btn btn-light-info' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToXLSX(`Concentracao-Vendedor-${timestamp}.xlsx`, rows)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em XLSX'>
              <i className='fas fa-file-excel me-2'></i> XLSX
            </button>
            <button className='btn btn-light-danger' onClick={() => {
              const timestamp = new Date().toISOString().split('T')[0]
              exportToPDF(`Concentracao-Vendedor-${timestamp}.pdf`, rows, report, curveAShare)
            }} disabled={loading || rows.length === 0} title='Baixar relatório em PDF'>
              <i className='fas fa-file-pdf me-2'></i> PDF
            </button>
          </div>
        }
        filters={
          <div className='row g-3 align-items-end'>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data inicial</label>
              <input
                className='form-control'
                type='date'
                value={filters.startDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, startDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-3'>
              <label className='form-label fw-semibold'>Data final</label>
              <input
                className='form-control'
                type='date'
                value={filters.finalDate}
                onChange={(event) =>
                  setFilters((previous) => ({ ...previous, finalDate: event.target.value }))
                }
              />
            </div>
            <div className='col-12 col-md-4'>
              <label className='form-label fw-semibold'>Fonte</label>
              <select
                className='form-select'
                value={filters.source}
                onChange={(event) => onSourceChange(event.target.value as SellerReportSourceFilter)}
              >
                {sourceOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className='col-12 col-md-2 d-grid'>
              <button className='btn btn-light-primary' onClick={loadReport} disabled={loading}>
                Buscar
              </button>
            </div>
          </div>
        }
      >
        <div className='row g-4 mb-4'>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Maior participação</div>
                <div className='fs-5 fw-bold text-dark'>
                  {topSeller ? <SellerIdentity label={topSeller.sellerLabel} /> : 'Sem dados'}
                </div>
                <div className='mt-2 fw-semibold text-primary'>
                  {topSeller ? `${percentFormatter.format(topSeller.revenueSharePercent)}%` : '-'}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Vendedores curva A</div>
                <div className='fs-2hx fw-bold text-success'>
                  {rows.filter((row) => row.curveClass === 'A').length.toLocaleString('pt-BR')}
                </div>
              </div>
            </div>
          </div>
          <div className='col-12 col-md-4'>
            <div className='card border-0 shadow-sm h-100'>
              <div className='card-body'>
                <div className='text-gray-600 fw-semibold mb-1'>Receita concentrada na curva A</div>
                <div className='fs-2hx fw-bold text-warning'>
                  {percentFormatter.format(curveAShare)}%
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className='card border-0 shadow-sm'>
          <div className='card-header border-0 pt-5'>
            <div className='card-title d-flex flex-column'>
              <span className='fs-3 fw-bold text-dark'>Curva ABC de vendedores</span>
              <span className='text-gray-600 mt-1'>Receita individual, acumulada e classificação por relevância.</span>
            </div>
          </div>
          <div className='card-body pt-0'>
            {loading ? (
              <div className='d-flex justify-content-center align-items-center py-10'>
                <div className='spinner-border text-primary' role='status'>
                  <span className='visually-hidden'>Carregando...</span>
                </div>
              </div>
            ) : rows.length > 0 ? (
              <div className='table-responsive'>
                <table className='table align-middle table-row-dashed fs-6 gy-3'>
                  <thead>
                    <tr className='text-start text-gray-600 fw-bold fs-7 text-uppercase gs-0'>
                      <th>Posição</th>
                      <th>Vendedor</th>
                      <th className='text-end'>Pedidos</th>
                      <th className='text-end'>Faturamento</th>
                      <th className='text-end'>Ticket médio</th>
                      <th className='text-end'>Participação</th>
                      <th className='text-end'>Acumulado</th>
                      <th className='text-end'>Curva</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => (
                      <tr key={row.sellerKey}>
                        <td>#{index + 1}</td>
                        <td>
                          <div className='fw-bold text-dark'>
                            <SellerIdentity label={row.sellerLabel} />
                          </div>
                        </td>
                        <td className='text-end fw-bold'>{row.orderCount}</td>
                        <td className='text-end fw-bold text-success'>
                          {formatCurrency(row.totalAmountCents)}
                        </td>
                        <td className='text-end'>{formatCurrency(row.averageTicketCents)}</td>
                        <td className='text-end'>{percentFormatter.format(row.revenueSharePercent)}%</td>
                        <td className='text-end'>
                          {percentFormatter.format(row.cumulativeRevenueSharePercent)}%
                        </td>
                        <td className='text-end'>
                          <span className={curveBadgeClass(row.curveClass)}>{row.curveClass}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className='alert alert-light-info mb-0'>
                Nenhum vendedor encontrado para os filtros selecionados.
              </div>
            )}
          </div>
        </div>
      </ReportShell>
    </>
  )
}

export { SellerConcentrationReportPage }